import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import DisplayPrice from '../DisplayPrice';
import MainSwitch from '../MUI/MainSwitch';
import styles from './VerticalListCheckboxItem.module.scss';

const VerticalListCheckboxItem = (props) => {
  const {
    price,
    discount,
    resource,
    item,
    hasActiveDesc,
    setActiveDesc,
    hidePrice,
    isFetchingPrice,
    isChecked,
    isDisabled,
    onItemChange,
    hasNarrowView,
  } = props;

  const { t } = useTranslation();

  const getItemLogo = () => (
    <div className={styles.itemLogo}>
      <img alt="" src={item.icon} />
    </div>
  );

  const getAmountValue = (amount) => (
    <DisplayPrice price={amount} hidePrice={hidePrice} isFetching={isFetchingPrice} />
  );

  const getAdditionalDataSection = (data) => (
    <div className={styles.additionalData}>
      {data.map(({ key, value, isAmount = false }) => (
        <div key={key}>
          {t(`${resource}.labels.${key}`)}
          <b>{isAmount ? getAmountValue(value) : value}</b>
        </div>
      ))}
    </div>
  );

  const additionalDataSection = !!item.additionalData
    && item.additionalData.length > 0
    && getAdditionalDataSection(item.additionalData);

  const handleClickMore = () => {
    if (hasActiveDesc) {
      setActiveDesc(null);
    } else {
      setActiveDesc(item.id);
    }
  };

  const itemDesc = useMemo(
    () => (
      <div className={cn(styles.itemDesc, !hasActiveDesc && styles.itemDescHidden)}>
        {t(`${resource}.data.${item.id}.desc`)}
      </div>
    ),
    [hasActiveDesc, item.id, resource, t],
  );

  const getItemContent = () => (
    <div className={styles.itemContent}>
      <div>
        <div className={styles.itemName}>{t(`${resource}.data.${item.id}.name`)}</div>
        {additionalDataSection}
      </div>
      {!hasNarrowView && itemDesc}
      <button type="button" className={styles.itemMore} onClick={handleClickMore}>
        {hasActiveDesc ? t('seeLess') : t('seeMore')}
      </button>
    </div>
  );

  const getItemPrice = () => (
    <div className={styles.itemPrice}>
      <div className={styles.prices}>
        <div className={styles.totalPrice}>
          <DisplayPrice
            price={price - discount}
            hidePrice={hidePrice}
            isFetching={isFetchingPrice}
          />
        </div>

        {!!discount && !isFetchingPrice && (
          <div className={styles.priceWithoutDiscount}>
            <DisplayPrice price={price} hidePrice={hidePrice} />
          </div>
        )}
      </div>
      <div className={styles.checkboxContainer}>
        <MainSwitch
          color="primary"
          size="small"
          disabled={isDisabled}
          checked={isChecked}
          onChange={(event) => {
            onItemChange(item.id, event.target.checked);
          }}
        />
      </div>
    </div>
  );

  if (hasNarrowView) {
    return (
      <div className={cn(styles.itemSection, styles.itemSectionSmall)}>
        <div className={styles.itemHeader}>
          {getItemLogo()}
          {getItemContent()}
          {getItemPrice()}
        </div>
        {itemDesc}
      </div>
    );
  }

  return (
    <div className={styles.itemSection}>
      {getItemLogo()}
      {getItemContent()}
      {getItemPrice()}
    </div>
  );
};

VerticalListCheckboxItem.propTypes = {
  price: PropTypes.number,
  discount: PropTypes.number,
  resource: PropTypes.string.isRequired,
  item: PropTypes.instanceOf(Object).isRequired,
  hasActiveDesc: PropTypes.bool,
  setActiveDesc: PropTypes.func,
  hidePrice: PropTypes.bool,
  isFetchingPrice: PropTypes.bool,
  isChecked: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onItemChange: PropTypes.func,
  hasNarrowView: PropTypes.bool,
};

VerticalListCheckboxItem.defaultProps = {
  price: null,
  discount: 0,
  hasActiveDesc: false,
  setActiveDesc: () => {},
  hidePrice: false,
  isFetchingPrice: false,
  isChecked: false,
  isDisabled: false,
  onItemChange: () => {},
  hasNarrowView: false,
};

export default VerticalListCheckboxItem;
